import { useState, useEffect } from 'react';
import Header from '../../../components/feature/Header';
import FoodFormModal from './components/FoodFormModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';

// const API_BASE_URL = "http://localhost:3111/api";
const API_BASE_URL = "https://break.dulcecomercio.com/api";

const FoodsAdminPage = () => {
  const [foods, setFoods] = useState([]);
const [showAddModal, setShowAddModal] = useState(false);
const [showEditModal, setShowEditModal] = useState(false);
const [showDeleteModal, setShowDeleteModal] = useState(false);
const [selectedFood, setSelectedFood] = useState(null);
const [searchTerm, setSearchTerm] = useState('');
const [filterCategory, setFilterCategory] = useState('all');
const [loading, setLoading] = useState(false);

//
// 🔹 Fetch all foods (GET /api/dulce/foods)
//
useEffect(() => {
  const fetchFoods = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/dulce/foods`);
      if (!res.ok) throw new Error('Failed to fetch foods');
      const data = await res.json();
      setFoods(Array.isArray(data) ? data : data.data || []); // handle array or wrapped data
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };
  fetchFoods();
}, []);

//
// 🔹 Add food (POST /api/dulce/foods)
//
const handleAddFood = async (foodData) => {
  try {
    const res = await fetch(`${API_BASE_URL}/dulce/foods`, {
      method: 'POST',
      body: foodData, // FormData directly
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to add food');

    const newFood = data.data || data; // always use .data if exists
    setFoods((prev) => [...prev, newFood]);
    setShowAddModal(false);
  } catch (err) {
    console.error('Add food error:', err);
    alert(err.message);
  }
};

//
// 🔹 Edit food (PUT /api/dulce/foods/:id)
//
const handleEditFood = async (foodData) => {
  try {
    const res = await fetch(`${API_BASE_URL}/dulce/foods/${selectedFood._id}`, {
      method: 'PUT',
      body: foodData, // FormData directly
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update food');

    const updatedFood = data.data || data;
    setFoods((prev) =>
      prev.map((food) =>
        food._id === selectedFood._id ? updatedFood : food
      )
    );

    setShowEditModal(false);
    setSelectedFood(null);
  } catch (err) {
    console.error('Edit error:', err);
    alert(err.message);
  }
};

//
// 🔹 Delete food (DELETE /api/dulce/foods/:id)
//
const handleDeleteFood = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/dulce/foods/${selectedFood._id}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error('Failed to delete food');

    setFoods((prev) => prev.filter((food) => food._id !== selectedFood._id));
    setShowDeleteModal(false);
    setSelectedFood(null);
  } catch (err) {
    console.error('Delete error:', err);
    alert(err.message);
  }
};

//
// 🔹 Open modals
//
const openEditModal = (food) => {
  setSelectedFood(food);
  setShowEditModal(true);
};

const openDeleteModal = (food) => {
  setSelectedFood(food);
  setShowDeleteModal(true);
};

//
// 🔹 Filtering logic (null-safe)
//
const filteredFoods = foods.filter((food) => {
  const name = food.name?.toLowerCase() || '';
  const restaurant = food.restaurant?.toLowerCase() || '';
  const category = food.category?.toLowerCase() || '';

  const matchesSearch =
    name.includes(searchTerm.toLowerCase()) ||
    restaurant.includes(searchTerm.toLowerCase());
  const matchesCategory =
    filterCategory === 'all' || category === filterCategory;

  return matchesSearch && matchesCategory;
});

//
// 🔹 Category list
//
const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'healthy', label: 'Healthy' },
  { value: 'premium', label: 'Premium' },
  { value: 'luxury', label: 'Luxury' },
];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="pt-20 pb-24 px-4">
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Food Management</h1>
          <p className="text-gray-600">Manage your food items and menu</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <i className="ri-restaurant-line text-orange-600"></i>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{foods.length}</p>
                <p className="text-sm text-gray-600">Total Foods</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-store-line text-green-600"></i>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">
                  {new Set(foods.map((f) => f.restaurant)).size}
                </p>
                <p className="text-sm text-gray-600">Restaurants</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="space-y-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="ri-search-line text-gray-400"></i>
              </div>
              <input
                type="text"
                placeholder="Search foods or restaurants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
              />
            </div>

            <div className="flex items-center justify-between">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
              >
                {categories.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>

              <button
                onClick={() => setShowAddModal(true)}
                className="bg-orange-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2"
              >
                <i className="ri-add-line"></i>
                <span>Add Food</span>
              </button>
            </div>
          </div>
        </div>

        {/* Loading or Foods List */}
        {loading ? (
          <div className="text-center py-12">Loading foods...</div>
        ) : (
          <div className="space-y-4">
            {filteredFoods.map((food) => (
              <div
                key={food._id}
                className="bg-white rounded-xl border border-gray-200 p-4"
              >
                <div className="flex items-start space-x-4">
                  <img
                    src={food.image}
                    alt={food.name}
                    className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800 mb-1">
                          {food.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {food.restaurant}
                        </p>
                        <div className="flex items-center space-x-3">
                          <span className="font-bold text-orange-500">
                            ${food.price}
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              food.category === 'healthy'
                                ? 'bg-green-100 text-green-600'
                                : food.category === 'premium'
                                ? 'bg-purple-100 text-purple-600'
                                : 'bg-yellow-100 text-yellow-600'
                            }`}
                          >
                            {food.category}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => openEditModal(food)}
                          className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors"
                        >
                          <i className="ri-edit-line text-blue-600 text-sm"></i>
                        </button>
                        <button
                          onClick={() => openDeleteModal(food)}
                          className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors"
                        >
                          <i className="ri-delete-bin-line text-red-600 text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filteredFoods.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-600">
                No foods found.
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      {showAddModal && (
        <FoodFormModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddFood}
          title="Add New Food"
        />
      )}
      {showEditModal && selectedFood && (
        <FoodFormModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedFood(null);
          }}
          onSubmit={handleEditFood}
          title="Edit Food"
          initialData={selectedFood}
        />
      )}
      {showDeleteModal && selectedFood && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedFood(null);
          }}
          onConfirm={handleDeleteFood}
          foodName={selectedFood.name}
        />
      )}
    </div>
  );
};

export default FoodsAdminPage;
